import { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import { IntakeContext } from '../context/IntakeContext'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
// import { Link } from "react-router-dom";

// @mui material components
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Icon from '@mui/material/Icon'
import Box from '@mui/material/Box'

// Soft UI Dashboard PRO React icons
import HelpIcon from '@mui/icons-material/Help'
import fehb from 'assets/images/fehb.svg'
import tricare from 'assets/images/tricare.png'

const StyledMSB = styled.div`
  text-align: center;
  position: relative;
  padding: 16px;
  height: 100%;
  cursor: pointer;
  border: ${(props) =>
    props.active ? '2px solid #a3a7aa' : '2px solid #e5e5e5'};
  background: ${(props) => (props.active ? '#eeeeee' : '#fff')};
  color: #212121;
  border-radius: 0.75rem;
  transition: 0.2s all ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    border: 2px solid #a3a7aa;
    background: #f5f5f5;
  }

  > h6,
  h5 {
    color: #212121;
    letter-spacing: 0.3px;
    font-weight: 600;
    line-height: 1.4;
    margin: 0;
  }

  > span {
    border-radius: 50%;
    height: 1.5em;
    width: 1.5em;
    padding: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    &.yesIcon {
      color: #4caf50;
      background-color: #dbefdc;
    }
    &.noIcon {
      color: #fd5c70;
      background-color: #fedee2;
    }
  }
`
const StyledAgencyLogo = styled.img`
  max-height: 60px;
  width: auto;
  max-width: 100%;
`

const HealthcareTileSelector = () => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)

  const healthcareProviders = {
    fehb: 'fehb',
    pshb: 'pshb',
    tricare: 'tricare',
    other: 'other',
  }

  const [healthcareProvider, setHealthcareProvider] = useState(
    intakeState.healthInsurance
  )

  const fehbLookup = intakeState?.fehbCode
    ? intakeState.fehbCode.replace('HP ', '').match(/^\d/)
      ? '_' + intakeState.fehbCode.replace('HP ', '')
      : intakeState.fehbCode
    : ''
  const suggestedPremium = selectData.fehb[fehbLookup]

  const toggleProvider = (name) => {
    setHealthcareProvider(name)
  }

  useEffect(() => {
    if (typeof healthcareProviders[healthcareProvider] != 'string') return
    updateIntakeStateAtKey({
      healthInsurance: healthcareProvider,
    })
  }, [healthcareProvider])

  useEffect(() => {
    if (!intakeState.fehbCode) return

    // compute or fetch premium once code is known
    const premium = suggestedPremium?.biweeklyEmp || 0

    if (premium) {
      updateIntakeStateAtKey({ healthInsurancePremium: premium })
    }
  }, [intakeState.fehbCode, suggestedPremium])

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box mb={1}>
        <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
          Which health insurance program are you a part of?
        </Typography>
      </Box>
      <Typography variant="body1" fontSize={{ xs: '16px', sm: '20px' }}>
        Select one of the following:
      </Typography>
      <Box my={4}>
        <Grid container spacing={1} justifyContent="center" mb={3}>
          <Grid item xs={4}>
            <StyledMSB
              active={healthcareProvider === 'fehb'}
              onClick={() => toggleProvider('fehb')}
            >
              <StyledAgencyLogo src={fehb} />
              <Typography variant="h6" textTransform="capitalize">
                FEHB
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={4}>
            <StyledMSB
              active={healthcareProvider === 'tricare'}
              onClick={() => toggleProvider('tricare')}
            >
              <StyledAgencyLogo src={tricare} />
              <Typography
                variant="h6"
                textTransform="capitalize"
                sx={{
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                Tricare
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={4}>
            <StyledMSB
              active={healthcareProvider === 'pshb'}
              onClick={() => toggleProvider('pshb')}
            >
              <StyledAgencyLogo src="https://uspsblog.com/wp-content/uploads/2023/09/USPS_Eagle-Symbol-web-size-300x253-1.png" />
              <Typography variant="h6" textTransform="capitalize">
                PSHB
              </Typography>
            </StyledMSB>
          </Grid>
          {/* <Grid item xs={12}>
            <StyledMSB
              active={healthcareProvider === 'other'}
              onClick={() => toggleProvider('other')}
            >
              <Icon fontSize="large">
                <HelpIcon />
              </Icon>
              <Typography variant="h6" textTransform="capitalize">
                Other
              </Typography>
            </StyledMSB>
          </Grid> */}
        </Grid>
        <Grid container>
          {healthcareProvider === 'fehb' || healthcareProvider === 'pshb' ? (
            <Grid item xs={12} mb={2}>
              <Grid item xs={12} mt={2} mb={2}>
                <Typography variant="body1Medium">
                  Select Your {healthcareProvider.toUpperCase()} Code
                </Typography>
              </Grid>
              {renderMuiFormField({
                autoFocus: false,
                id: 'fehbCode',
                label: `${healthcareProvider.toUpperCase()} Code`,
                placeholder: '',
                inputType: 'select',
                value: intakeState.fehbCode,
                searchable: true,
                onChange: (fehbCode) => updateIntakeStateAtKey({ fehbCode }),
                options:
                  healthcareProvider === 'fehb'
                    ? selectData.fehbCode.slice(0, 438)
                    : selectData.fehbCode.slice(-234),
              })}
              {intakeState?.fehbCode && (
                <Typography variant="inputNote" component="p">
                  {suggestedPremium?.type
                    ? `Plan Type: ${suggestedPremium?.type}`
                    : ''}
                </Typography>
              )}
            </Grid>
          ) : (
            <></>
          )}
          <Grid item xs={12} mb={2}>
            <Grid item xs={12} mt={2} mb={2}>
              <Typography variant="body1Medium">
                What is your healthcare premium per pay period?
              </Typography>
            </Grid>

            {renderMuiFormField({
              autoFocus: false,
              id: 'healthInsurancePremium',
              label: 'Premium Per Pay Period',
              placeholder: `${
                (healthcareProvider === 'fehb' ||
                  healthcareProvider === 'pshb') &&
                intakeState?.fehbCode
                  ? '$' + suggestedPremium?.biweeklyEmp
                  : '$275.00'
              }`,
              inputType: 'currencyFloat',
              value: intakeState.healthInsurancePremium,
              onChange: (healthInsurancePremium) =>
                updateIntakeStateAtKey({ healthInsurancePremium }),
            })}
            {(healthcareProvider === 'fehb' || healthcareProvider === 'pshb') &&
            intakeState?.fehbCode ? (
              <Typography variant="inputNote" component="p">
                Suggested Premium Based on Code: $
                {suggestedPremium?.biweeklyEmp}
              </Typography>
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default HealthcareTileSelector
